import "./GenericGrid.scss";

import {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
  ColumnState,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { GridDataAdaptor, DataProvider } from "./GridDataAdaptor";
import { LegacyRef, useEffect, useRef, useState, useMemo } from "react";
import { FilterCriteria, SortCriteria } from "patients-experience-client";
import "ag-grid-enterprise";
import { useFillHeight } from "./useFillHeight";

const pageSizes = [10, 25, 50];
const pageSize = pageSizes[2];

const getCachedColDefs = (colDefs: ColDef[], gridId?: string): ColDef[] => {
  if (!gridId) return colDefs;

  const savedState = localStorage.getItem(`grid-column-state-${gridId}`);
  if (!savedState) return colDefs;

  try {
    const columnState: ColumnState[] = JSON.parse(savedState);
    const currentFields = new Set(
      colDefs.map((col) => col.field).filter(Boolean),
    );
    const cachedFields = new Set(columnState.map((state) => state.colId));

    // account for columns that were removed or renamed
    const validColumns = columnState.filter((state) =>
      currentFields.has(state.colId),
    );

    if (validColumns.length !== columnState.length) {
      localStorage.setItem(
        `grid-column-state-${gridId}`,
        JSON.stringify(validColumns),
      );
    }

    const orderedCols = validColumns
      .map((state) => colDefs.find((col) => col.field === state.colId))
      .filter((col): col is ColDef => col !== undefined);

    const newColumns = colDefs.filter(
      (col) => col.field && !cachedFields.has(col.field),
    );

    const fieldlessColumns = colDefs.filter((col) => !col.field);
    return [...orderedCols, ...newColumns, ...fieldlessColumns];
  } catch {
    return colDefs;
  }
};

export interface Props {
  colDefs: ColDef[];
  dataProvider: DataProvider;
  filters: FilterCriteria[];
  /** Force a fixed height (e.g. "350px") for grids that should NOT fill — e.g. several stacked
   *  on one page. Omit it and the grid fills the space available below it (the default). */
  fixedHeight?: string;
  pagination?: boolean;
  onRowClicked?: (event: RowClickedEvent) => void;
  sortCriteria?: SortCriteria;
  gridId: string;
}

export const GenericGrid = (props: Props) => {
  const [gridApi, setGridApi] = useState<GridApi>();

  const [showNoRowsOverlay, setShowNoRowsOverlay] = useState<boolean>(false);

  const orderedColDefs = useMemo(
    () => getCachedColDefs(props.colDefs, props.gridId),
    [props.colDefs, props.gridId],
  );

  const [gridDataAdaptor, setGridDataAdaptor] = useState<GridDataAdaptor>(
    new GridDataAdaptor((show) => setShowNoRowsOverlay(show)),
  );
  const gridRef = useRef<AgGridReact>(null);

  // Unless a fixed height is requested, the grid fills the space available below it (measured, not
  // a per-page hardcoded offset — see useFillHeight).
  const { ref: containerRef, height: fillHeight } = useFillHeight<HTMLDivElement>(!props.fixedHeight);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    editable: false,
    suppressMenu: true,
  };

  useEffect(() => {
    gridDataAdaptor.setDataProvider(props.dataProvider);
    setGridDataAdaptor(gridDataAdaptor);
  }, [props.dataProvider]);

  useEffect(() => {
    gridDataAdaptor.setFilters(props.filters);
    gridApi?.refreshServerSide({ purge: true });
  }, [props.filters]);

  const saveColumnState = () => {
    if (props.gridId && gridApi) {
      const columnState = gridApi.getColumnState();
      localStorage.setItem(
        `grid-column-state-${props.gridId}`,
        JSON.stringify(columnState),
      );
    }
  };

  const onGridReady = (event: GridReadyEvent<any, any>) => {
    setGridApi(event.api);
    try {
      if (props.sortCriteria) {
        gridRef.current?.api.applyColumnState({
          state: [
            {
              colId: props.sortCriteria?.propertyName,
              sort: props.sortCriteria.descending ? "desc" : "asc",
            },
          ],
        });
      }
    } catch (error) {
      console.error("Error while sorting care note:", error);
    }
  };

  return (
    <div
      ref={containerRef}
      className={"generic-grid ag-theme-quartz"}
      style={{
        width: "100%",
        height: props.fixedHeight ?? fillHeight ?? "",
      }}
    >
      <AgGridReact
        gridId={props.gridId}
        columnDefs={orderedColDefs}
        defaultColDef={defaultColDef}
        serverSideDatasource={gridDataAdaptor}
        rowModelType="serverSide"
        pagination={props.pagination ?? true}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={pageSizes}
        cacheBlockSize={pageSize}
        suppressDragLeaveHidesColumns={true}
        suppressColumnMoveAnimation={true}
        suppressMovableColumns={false}
        suppressNoRowsOverlay={false}
        onGridReady={onGridReady}
        onColumnMoved={saveColumnState}
        onRowClicked={props.onRowClicked}
        ref={gridRef}
        blockLoadDebounceMillis={1000}
        maxConcurrentDatasourceRequests={1}
        suppressContextMenu={true}
        autoSizeStrategy={{ type: "fitCellContents" }}
      />
      {showNoRowsOverlay && (
        <div className="no-rows-overlay">No rows to show</div>
      )}
    </div>
  );
};
